import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import HeaderAndTag from "./form/HeaderAndTag";

const AnnualSnapshot = () => {
  const [counts, setCounts] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyCache, setHistoryCache] = useState({});
  const { jwtToken } = useOutletContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (jwtToken === "") {
      navigate("/login");
      return;
    }

    setLoading(true);
    
    // First get all counters
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + jwtToken);

    const requestOptions = {
      method: "GET",
      headers: headers,
    };

    fetch(`${process.env.REACT_APP_BACKEND}/counts`, requestOptions)
      .then((response) => response.json())
      .then(async (counters) => {
        const cache = {};
        const allYears = new Set();
        
        // Fetch history for each counter and extract years
        for (const counter of counters) {
          try {
            const historyResponse = await fetch(
              `${process.env.REACT_APP_BACKEND}/counts/${counter.id}/history`,
              requestOptions
            );
            const historyData = await historyResponse.json();
            
            // Cache the history data
            cache[counter.id] = historyData;
            
            // Extract years from history entries
            historyData.history.forEach(entry => {
              const year = new Date(entry.timestamp).getFullYear();
              allYears.add(year);
            });
          } catch (err) {
            console.log(`Error fetching history for ${counter.name}:`, err);
          }
        }
        
        const sortedYears = Array.from(allYears).sort((a, b) => b - a);
        setHistoryCache(cache);
        setAvailableYears(sortedYears);
        if (sortedYears.length > 0) {
          setSelectedYear(sortedYears[0]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [jwtToken, navigate]);

  useEffect(() => {
    if (selectedYear && Object.keys(historyCache).length > 0) {
      // Calculate from cached data
      const yearCounts = Object.entries(historyCache)
        .map(([counterId, data]) => {
          const yearTotal = data.history
            .filter(entry => new Date(entry.timestamp).getFullYear() === parseInt(selectedYear))
            .reduce((sum, entry) => sum + entry.value, 0);
          
          return {
            id: counterId,
            name: data.counterName,
            totalCount: yearTotal
          };
        })
        .filter(counter => counter.totalCount > 0);
      setCounts(yearCounts);
    }
  }, [selectedYear, historyCache]);

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  return (
    <div>
      <HeaderAndTag title="Annual Snapshot" />
      
      <div className="mb-3">
        <label htmlFor="yearSelect" className="form-label">Select Year:</label>
        <select 
          id="yearSelect"
          className="form-select" 
          value={selectedYear} 
          onChange={handleYearChange}
        >
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {loading && <p>Loading data ...</p>}

      {!loading && selectedYear && (
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Counter</th>
              <th>Total Count for {selectedYear}</th>
            </tr>
          </thead>
          <tbody>
            {counts.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.totalCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AnnualSnapshot;